import { PrizeType } from '@common/contracts';
import { getCoordinateString, parseCoordinateString } from '../../utils/coordinates';
const DIRS = [[0, -1], [1, 0], [0, 1], [-1, 0]];
/** Parse an Excel-style coord (e.g. "B10") into 0-based {x,y}. */
export const parseCoord = parseCoordinateString;
function eventMap(events) {
    return new Map(events.map(e => [e.id, e]));
}
function nodeEncounters(node, em) {
    var _a, _b;
    const out = [];
    for (const se of (_a = node.simpleEvents) !== null && _a !== void 0 ? _a : []) {
        const ev = em.get(se.eventId);
        if (ev)
            out.push(...((_b = ev.encounters) !== null && _b !== void 0 ? _b : []));
    }
    return out;
}
const hasTrial = (node) => { var _a, _b; return ((_b = (_a = node.simpleEvents) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0; };
const rewards = (enc) => enc.rewardAmount > 0 && (enc.rewardType !== PrizeType.Resource || !!enc.rewardResourceName);
const trialGivesSomething = (encs) => encs.some(rewards);
function nodeScannable(node) {
    var _a, _b;
    return !!node.hasScannableResources && ((_b = (_a = node.scannableResourcesList) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0;
}
function nodeResourceReward(node, encs) {
    var _a;
    if (encs.some(e => e.rewardType === PrizeType.Resource && e.rewardAmount > 0 && !!e.rewardResourceName))
        return true;
    return ((_a = node.prizesWithoutTrial) !== null && _a !== void 0 ? _a : []).some(p => p.type === PrizeType.Resource);
}
const nodeDistributesResource = (node, encs) => nodeScannable(node) || nodeResourceReward(node, encs);
function sumByType(node, encs, type) {
    var _a;
    let total = 0;
    for (const e of encs)
        if (e.rewardType === type && e.rewardAmount > 0)
            total += e.rewardAmount;
    for (const p of (_a = node.prizesWithoutTrial) !== null && _a !== void 0 ? _a : [])
        if (p.type === type && p.amount > 0)
            total += p.amount;
    return total;
}
export function computeDistribution(level, events = [], startTypeId) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const em = eventMap(events);
    const stats = {
        trialNodeCount: 0, trialInstanceCount: 0,
        byRewardType: { [PrizeType.Money]: 0, [PrizeType.Knowledge]: 0, [PrizeType.Resource]: 0 },
        givesNothingCount: 0, byAbility: {},
        knowledgeTotal: 0, knowledgeNodeCount: 0, knowledgeMin: 0, knowledgeMax: 0, costTotal: 0, moneyTotal: 0, moneyNodeCount: 0,
        resourceTotal: 0,
        scannableNodeCount: 0, resourceRewardCount: 0, resourceNodeCount: 0,
        distinctResourceCount: 0, startPointCount: 0,
    };
    const resourceIds = new Set();
    const trialEmpty = [];
    const resourceEmpty = [];
    const resourceNoTrial = [];
    const usedEventIds = new Set();
    for (const [coord, node] of Object.entries((_a = level.nodes) !== null && _a !== void 0 ? _a : {})) {
        if (!node)
            continue;
        // The start point is the cell assigned the "Start" node type.
        if (startTypeId && node.typeId === startTypeId)
            stats.startPointCount++;
        const encs = nodeEncounters(node, em);
        for (const se of (_b = node.simpleEvents) !== null && _b !== void 0 ? _b : [])
            usedEventIds.add(se.eventId);
        const trial = hasTrial(node);
        if (trial) {
            stats.trialNodeCount++;
            stats.trialInstanceCount += node.simpleEvents.length;
            for (const e of encs)
                stats.byAbility[e.ability] = ((_c = stats.byAbility[e.ability]) !== null && _c !== void 0 ? _c : 0) + 1;
            const rewarding = encs.find(rewards);
            if (rewarding)
                stats.byRewardType[rewarding.rewardType]++;
            else {
                stats.givesNothingCount++;
                trialEmpty.push(coord);
            }
        }
        for (const e of encs)
            stats.costTotal += e.powerCost || 0;
        const k = sumByType(node, encs, PrizeType.Knowledge);
        if (k > 0) {
            stats.knowledgeTotal += k;
            stats.knowledgeNodeCount++;
        }
        // Received-Knowledge range: one approach picked per trial (event). Each event
        // contributes between its min and max Knowledge approach (a non-Knowledge
        // approach counts as 0 Knowledge). Non-trial prizes are guaranteed → both bounds.
        for (const se of (_d = node.simpleEvents) !== null && _d !== void 0 ? _d : []) {
            const ev = em.get(se.eventId);
            if (!ev || !((_e = ev.encounters) === null || _e === void 0 ? void 0 : _e.length))
                continue;
            const kVals = ev.encounters.map(e => e.rewardType === PrizeType.Knowledge && e.rewardAmount > 0 ? e.rewardAmount : 0);
            stats.knowledgeMin += Math.min(...kVals);
            stats.knowledgeMax += Math.max(...kVals);
        }
        for (const p of (_f = node.prizesWithoutTrial) !== null && _f !== void 0 ? _f : []) {
            if (p.type === PrizeType.Knowledge && p.amount > 0) {
                stats.knowledgeMin += p.amount;
                stats.knowledgeMax += p.amount;
            }
        }
        const m = sumByType(node, encs, PrizeType.Money);
        if (m > 0) {
            stats.moneyTotal += m;
            stats.moneyNodeCount++;
        }
        stats.resourceTotal += sumByType(node, encs, PrizeType.Resource);
        if (nodeScannable(node)) {
            stats.scannableNodeCount++;
            ((_g = node.scannableResourcesList) !== null && _g !== void 0 ? _g : []).forEach(id => resourceIds.add(id));
        }
        for (const e of encs)
            if (e.rewardType === PrizeType.Resource && e.rewardAmount > 0 && e.rewardResourceName) {
                stats.resourceRewardCount++;
                resourceIds.add(e.rewardResourceName);
            }
        for (const p of (_h = node.prizesWithoutTrial) !== null && _h !== void 0 ? _h : [])
            if (p.type === PrizeType.Resource) {
                stats.resourceRewardCount++;
                if (p.resourceName)
                    resourceIds.add(p.resourceName);
            }
        if (nodeDistributesResource(node, encs))
            stats.resourceNodeCount++;
        // Per-node validation
        if (trial && !trialGivesSomething(encs)) { /* recorded in trialEmpty above */ }
        const flaggedButEmpty = (!!node.hasScannableResources && ((_k = (_j = node.scannableResourcesList) === null || _j === void 0 ? void 0 : _j.length) !== null && _k !== void 0 ? _k : 0) === 0) ||
            encs.some(e => e.rewardType === PrizeType.Resource && e.rewardAmount > 0 && !e.rewardResourceName) ||
            ((_l = node.prizesWithoutTrial) !== null && _l !== void 0 ? _l : []).some(p => p.type === PrizeType.Resource && !p.resourceName);
        if (flaggedButEmpty)
            resourceEmpty.push(coord);
        if (!trial && nodeDistributesResource(node, encs))
            resourceNoTrial.push(coord);
    }
    stats.distinctResourceCount = resourceIds.size;
    const issues = [];
    const uniq = (a) => Array.from(new Set(a));
    if (stats.resourceNodeCount === 0)
        issues.push({ key: 'no-resources', severity: 'error', label: 'No resources are distributed anywhere on the map.', coords: [] });
    if (stats.knowledgeTotal === 0)
        issues.push({ key: 'no-knowledge', severity: 'error', label: 'No Knowledge is distributed anywhere.', coords: [] });
    if (stats.startPointCount === 0)
        issues.push({ key: 'no-start', severity: 'error', label: 'No start point is set on the map.', coords: [] });
    else if (stats.startPointCount > 1)
        issues.push({ key: 'multi-start', severity: 'error', label: `${stats.startPointCount} start points are set — there must be exactly one.`, coords: [] });
    if (trialEmpty.length)
        issues.push({ key: 'trial-empty', severity: 'error', label: `${trialEmpty.length} obstacle(s) give no reward.`, coords: uniq(trialEmpty) });
    if (resourceEmpty.length)
        issues.push({ key: 'resource-empty', severity: 'error', label: `${uniq(resourceEmpty).length} node(s) are set to give resources but specify none.`, coords: uniq(resourceEmpty) });
    if (resourceNoTrial.length)
        issues.push({ key: 'resource-no-trial', severity: 'warning', label: `${uniq(resourceNoTrial).length} node(s) give resources but have no obstacle.`, coords: uniq(resourceNoTrial) });
    const orphans = events.filter(e => { var _a; return ((_a = e.encounters) !== null && _a !== void 0 ? _a : []).some(en => en.rewardAmount > 0) && !usedEventIds.has(e.id); });
    if (orphans.length)
        issues.push({ key: 'orphan-events', severity: 'warning', label: `${orphans.length} configured obstacle(s) are not used by any cell.`, coords: [] });
    return { stats, issues };
}
export const DEFAULT_DISTRIBUTION_RULES = {
    scaleByDepth: true,
    coins: { enabled: true, weight: 2, minAmount: 50, maxAmount: 250 },
    data: { enabled: true, weight: 1, minAmount: 30, maxAmount: 150 },
    resource: { enabled: true, weight: 1, minAmount: 1, maxAmount: 1 },
    resourceId: null,
};
function ruleFor(rules, type) {
    return type === PrizeType.Money ? rules.coins : type === PrizeType.Knowledge ? rules.data : rules.resource;
}
function amountFor(rule, t, scaleByDepth) {
    const lo = Math.min(rule.minAmount, rule.maxAmount);
    const hi = Math.max(rule.minAmount, rule.maxAmount);
    const v = scaleByDepth ? lo + t * (hi - lo) : (lo + hi) / 2;
    return Math.max(1, Math.round(v));
}
function computeDepths(level, startCoord) {
    var _a;
    const depths = {};
    const coords = Object.keys((_a = level.nodes) !== null && _a !== void 0 ? _a : {});
    const startP = startCoord ? parseCoord(startCoord) : null;
    if (!startCoord || !startP || !coords.length) {
        coords.forEach(c => (depths[c] = 0));
        return depths;
    }
    const present = new Set(coords);
    const queue = [startCoord];
    depths[startCoord] = 0;
    while (queue.length) {
        const cur = queue.shift();
        const p = parseCoord(cur);
        for (const [dx, dy] of DIRS) {
            const nc = getCoordinateString(p.x + dx, p.y + dy);
            if (present.has(nc) && depths[nc] === undefined) {
                depths[nc] = depths[cur] + 1;
                queue.push(nc);
            }
        }
    }
    for (const c of coords)
        if (depths[c] === undefined) {
            const p = parseCoord(c);
            depths[c] = p ? Math.abs(p.x - startP.x) + Math.abs(p.y - startP.y) : 0;
        }
    return depths;
}
function medianByDepth(nodes, depths) {
    if (!nodes.length)
        return null;
    const sorted = [...nodes].sort((a, b) => { var _a, _b; return ((_a = depths[a[0]]) !== null && _a !== void 0 ? _a : 0) - ((_b = depths[b[0]]) !== null && _b !== void 0 ? _b : 0); });
    return sorted[Math.floor(sorted.length / 2)];
}
// The start point is the cell assigned the "Start" node type. If none exists and
// the Start type id is known, assign the lowest (y,x) cell to it (keeping its other
// data). Mutates nodePatches; returns the start coord + whether one was just set.
function ensureStart(entries, nodePatches, conflicts, startTypeId) {
    var _a;
    const starts = startTypeId ? entries.filter(([, n]) => n.typeId === startTypeId).map(([c]) => c) : [];
    if (starts.length > 1)
        conflicts.push(`${starts.length} cells use the Start type — there must be exactly one.`);
    if (starts.length >= 1)
        return { startCoord: starts[0], startPointSet: false };
    if (startTypeId && entries.length) {
        const ordered = entries.map(([c]) => c).filter(c => parseCoord(c))
            .sort((a, b) => { const pa = parseCoord(a), pb = parseCoord(b); return pa.y - pb.y || pa.x - pb.x; });
        if (ordered.length) {
            nodePatches[ordered[0]] = Object.assign(Object.assign({}, ((_a = nodePatches[ordered[0]]) !== null && _a !== void 0 ? _a : {})), { typeId: startTypeId });
            return { startCoord: ordered[0], startPointSet: true };
        }
    }
    return { startPointSet: false };
}
export function planAutoDistribute(level, events, rules = DEFAULT_DISTRIBUTION_RULES, resourceIds = [], startTypeId) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const em = eventMap(events);
    const entries = Object.entries((_a = level.nodes) !== null && _a !== void 0 ? _a : {}).filter((e) => !!e[1]);
    const nodePatches = {};
    const eventPatches = [];
    const conflicts = [];
    const { startCoord, startPointSet } = ensureStart(entries, nodePatches, conflicts, startTypeId);
    const depths = computeDepths(level, startCoord);
    const maxDepth = Math.max(0, ...Object.values(depths));
    const depthT = (coord) => { var _a; return (maxDepth > 0 ? ((_a = depths[coord]) !== null && _a !== void 0 ? _a : 0) / maxDepth : 0); };
    const chosenResource = (rules.resourceId && resourceIds.includes(rules.resourceId)) ? rules.resourceId : resourceIds[0];
    // Weighted plan of which type each empty trial gets (Resource only if one exists).
    const typePlan = [];
    const addType = (type, rule) => {
        if (!rule.enabled || rule.weight <= 0)
            return;
        for (let i = 0; i < Math.max(1, Math.round(rule.weight)); i++)
            typePlan.push(type);
    };
    addType(PrizeType.Money, rules.coins);
    addType(PrizeType.Knowledge, rules.data);
    if (chosenResource)
        addType(PrizeType.Resource, rules.resource);
    if (!typePlan.length)
        typePlan.push(PrizeType.Money);
    const eventRefCount = new Map();
    for (const [, n] of entries)
        for (const se of (_b = n.simpleEvents) !== null && _b !== void 0 ? _b : [])
            eventRefCount.set(se.eventId, ((_c = eventRefCount.get(se.eventId)) !== null && _c !== void 0 ? _c : 0) + 1);
    // Track which types the map already covers (so we can guarantee enabled ones).
    const covered = new Set();
    for (const [, n] of entries) {
        const enc = (_h = (_g = em.get((_f = (_e = (_d = n.simpleEvents) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.eventId) !== null && _f !== void 0 ? _f : '')) === null || _g === void 0 ? void 0 : _g.encounters) === null || _h === void 0 ? void 0 : _h[0];
        if (enc && rewards(enc))
            covered.add(enc.rewardType);
        if (nodeDistributesResource(n, nodeEncounters(n, em)))
            covered.add(PrizeType.Resource);
    }
    let rot = 0;
    let trialsFilled = 0;
    const patchedEvents = new Set();
    for (const [coord, node] of entries) {
        if (!hasTrial(node))
            continue;
        const ev = em.get(node.simpleEvents[0].eventId);
        const enc = (_j = ev === null || ev === void 0 ? void 0 : ev.encounters) === null || _j === void 0 ? void 0 : _j[0];
        if (!ev || !enc || rewards(enc) || patchedEvents.has(ev.id))
            continue;
        const type = typePlan[rot++ % typePlan.length];
        const reward = { rewardType: type, rewardAmount: amountFor(ruleFor(rules, type), depthT(coord), rules.scaleByDepth) };
        if (type === PrizeType.Resource)
            reward.rewardResourceName = chosenResource;
        eventPatches.push({ eventId: ev.id, encounterIndex: 0, reward });
        patchedEvents.add(ev.id);
        trialsFilled++;
        covered.add(type);
        if (((_k = eventRefCount.get(ev.id)) !== null && _k !== void 0 ? _k : 0) > 1)
            conflicts.push(`Event "${ev.title}" is shared by ${eventRefCount.get(ev.id)} cells — changing its reward affects all of them.`);
    }
    // Coverage: guarantee every ENABLED type appears at least once.
    const trialNodes = entries.filter(([, n]) => hasTrial(n));
    const forcedTypes = [];
    const ensure = (type, rule, label) => {
        var _a;
        if (!rule.enabled || covered.has(type))
            return;
        if (type === PrizeType.Resource && !chosenResource)
            return;
        const target = medianByDepth(trialNodes, depths);
        const ev = target ? em.get(target[1].simpleEvents[0].eventId) : undefined;
        if (!target || !((_a = ev === null || ev === void 0 ? void 0 : ev.encounters) === null || _a === void 0 ? void 0 : _a.length))
            return;
        if (rewards(ev.encounters[0]))
            conflicts.push(`Reward on "${ev.title}" overwritten to ${label} to guarantee it appears.`);
        const reward = Object.assign({ rewardType: type, rewardAmount: amountFor(rule, depthT(target[0]), rules.scaleByDepth) }, (type === PrizeType.Resource ? { rewardResourceName: chosenResource } : {}));
        const i = eventPatches.findIndex(p => p.eventId === ev.id);
        if (i >= 0)
            eventPatches[i] = { eventId: ev.id, encounterIndex: 0, reward };
        else
            eventPatches.push({ eventId: ev.id, encounterIndex: 0, reward });
        covered.add(type);
        forcedTypes.push(label);
    };
    ensure(PrizeType.Knowledge, rules.data, 'Knowledge');
    ensure(PrizeType.Resource, rules.resource, 'Resource');
    return {
        nodePatches, eventPatches,
        conflicts: Array.from(new Set(conflicts)),
        summary: { trialsFilled, startPointSet, forcedTypes },
    };
}
// Split `total` (>0) across `count` slots as integers summing exactly to total.
// Each slot gets at least floor(total/count); the remainder lands on the last
// (deepest) slots so deeper cells lean slightly richer. Requires count <= total
// so every slot gets >= 1.
function spreadAmount(total, count) {
    if (count <= 0)
        return [];
    const base = Math.floor(total / count);
    const remainder = total - base * count;
    return Array.from({ length: count }, (_, i) => base + (i >= count - remainder ? 1 : 0));
}
export function planTargetedDistribute(level, events, opts) {
    var _a, _b, _c, _d, _e, _f;
    const em = eventMap(events);
    const entries = Object.entries((_a = level.nodes) !== null && _a !== void 0 ? _a : {}).filter((e) => !!e[1]);
    const nodePatches = {};
    const eventPatches = [];
    const conflicts = [];
    const { startCoord, startPointSet } = ensureStart(entries, nodePatches, conflicts, opts.startTypeId);
    const depths = computeDepths(level, startCoord);
    // How much is already placed — top up only the remainder so the final total
    // matches the target exactly.
    const { stats } = computeDistribution(level, events);
    const remainingKnowledge = Math.max(0, Math.round(opts.targetKnowledge) - stats.knowledgeTotal);
    const remainingResources = Math.max(0, Math.round(opts.targetResources) - stats.resourceTotal);
    const remainingMoney = Math.max(0, Math.round((_b = opts.targetMoney) !== null && _b !== void 0 ? _b : 0) - stats.moneyTotal);
    const chosenResource = (opts.resourceId && opts.availableResourceIds.includes(opts.resourceId))
        ? opts.resourceId : opts.availableResourceIds[0];
    if (opts.targetResources > stats.resourceTotal && remainingResources > 0 && !chosenResource) {
        conflicts.push('No resource type is available to distribute — create a resource first.');
    }
    // Empty trials only (a trial whose first encounter gives no reward), deduped by
    // event and ordered shallow→deep so the spread's remainder enriches deeper cells.
    // Events shared by several cells are SKIPPED: computeDistribution counts a
    // reward once per referencing cell, so patching a shared event would add its
    // amount N times and overshoot the exact target. We only place on events
    // unique to one cell, which keeps the final total exact.
    const seenEvents = new Set();
    const emptyTrials = [];
    const eventRefCount = new Map();
    for (const [, n] of entries)
        for (const se of (_c = n.simpleEvents) !== null && _c !== void 0 ? _c : [])
            eventRefCount.set(se.eventId, ((_d = eventRefCount.get(se.eventId)) !== null && _d !== void 0 ? _d : 0) + 1);
    for (const [coord, node] of entries) {
        if (!hasTrial(node))
            continue;
        const ev = em.get(node.simpleEvents[0].eventId);
        const enc = (_e = ev === null || ev === void 0 ? void 0 : ev.encounters) === null || _e === void 0 ? void 0 : _e[0];
        if (!ev || !enc || rewards(enc) || seenEvents.has(ev.id))
            continue;
        seenEvents.add(ev.id);
        if (((_f = eventRefCount.get(ev.id)) !== null && _f !== void 0 ? _f : 1) > 1) {
            conflicts.push(`Event "${ev.title}" is shared by ${eventRefCount.get(ev.id)} cells — skipped so the total stays exact. Give those cells their own events to include them.`);
            continue;
        }
        emptyTrials.push({ coord, eventId: ev.id, title: ev.title });
    }
    emptyTrials.sort((a, b) => { var _a, _b; return ((_a = depths[a.coord]) !== null && _a !== void 0 ? _a : 0) - ((_b = depths[b.coord]) !== null && _b !== void 0 ? _b : 0); });
    const demands = [];
    if (remainingMoney > 0)
        demands.push({ type: PrizeType.Money, label: 'coins', remaining: remainingMoney, trials: [] });
    if (remainingKnowledge > 0)
        demands.push({ type: PrizeType.Knowledge, label: 'data', remaining: remainingKnowledge, trials: [] });
    if (remainingResources > 0 && chosenResource)
        demands.push({ type: PrizeType.Resource, label: 'resources', remaining: remainingResources, resourceName: chosenResource, trials: [] });
    // Allocate empty trials across the demands round-robin, capping each at its
    // remaining amount so every used trial gets >= 1 after spreading.
    let cursor = 0;
    for (const t of emptyTrials) {
        let placed = false;
        for (let n = 0; n < demands.length; n++) {
            const d = demands[(cursor + n) % demands.length];
            if (d.trials.length < d.remaining) {
                d.trials.push(t);
                cursor = (cursor + n + 1) % demands.length;
                placed = true;
                break;
            }
        }
        if (!placed)
            break; // every demand is full
    }
    // Every empty trial here is unique to one cell (shared events were skipped
    // above), so a placed amount contributes exactly once to the level total.
    const pushReward = (t, reward) => eventPatches.push({ eventId: t.eventId, encounterIndex: 0, reward });
    let trialsFilled = 0;
    for (const d of demands) {
        spreadAmount(d.remaining, d.trials.length).forEach((amount, i) => pushReward(d.trials[i], Object.assign({ rewardType: d.type, rewardAmount: amount }, (d.resourceName ? { rewardResourceName: d.resourceName } : {}))));
        trialsFilled += d.trials.length;
        if (d.trials.length === 0)
            conflicts.push(`Not enough empty obstacles to distribute ${d.label} — add more obstacle cells.`);
    }
    return {
        nodePatches, eventPatches,
        conflicts: Array.from(new Set(conflicts)),
        summary: { trialsFilled, startPointSet, forcedTypes: [] },
    };
}
